import { useState } from 'react';

export default function Microsoft365Page() {
  // New state variables from modified content
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCustomerToolsDropdown, setShowCustomerToolsDropdown] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);

  // Original state variables (renamed where necessary)
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.currentTarget;
      const formDataObj = new FormData(form);

      const message = formDataObj.get('message') as string;
      if (message && message.length > 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Mesaj 500 karakterden uzun olamaz.'
        });
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(
        'https://lbvcnepwlojmojlxmhau.supabase.co/functions/v1/send-contact-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fullName: formDataObj.get('fullName'),
            phone: formDataObj.get('phone'),
            email: formDataObj.get('email'),
            company: formDataObj.get('company'),
            message: formDataObj.get('message')
          })
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Sunucudan geçerli bir yanıt alınamadı.');
      }

      if (result?.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Talebiniz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.'
        });
        form.reset();
        setTimeout(() => {
          setIsContactModalOpen(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(result?.error || 'Form gönderimi başarısız');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      setSubmitStatus({
        type: 'error',
        message:
          'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    {
      icon: 'ri-mail-line',
      title: 'Exchange Online',
      description: 'Profesyonel e-posta çözümleri, takvim ve kişi yönetimi',
      features: ['50 GB posta kutusu', 'Mobil erişim', 'Spam koruması', 'Arşivleme']
    },
    {
      icon: 'ri-team-line',
      title: 'Microsoft Teams',
      description: 'Video konferans, sohbet ve işbirliği platformu',
      features: [
        'HD video konferans',
        'Ekran paylaşımı',
        'Dosya işbirliği',
        'Entegrasyonlar'
      ]
    },
    {
      icon: 'ri-file-text-line',
      title: 'SharePoint Online',
      description: 'Belge yönetimi ve intranet çözümleri',
      features: [
        'Belge kütüphanesi',
        'Workflow otomasyonu',
        'İçerik yönetimi',
        'Güvenlik'
      ]
    },
    {
      icon: 'ri-cloud-line',
      title: 'OneDrive for Business',
      description: 'Güvenli bulut depolama ve dosya senkronizasyonu',
      features: [
        '1 TB depolama',
        'Dosya senkronizasyonu',
        'Paylaşım kontrolü',
        'Sürüm geçmişi'
      ]
    },
    {
      icon: 'ri-file-excel-line',
      title: 'Office Uygulamaları',
      description: 'Word, Excel, PowerPoint ve diğer Office uygulamaları',
      features: [
        'Desktop uygulamalar',
        'Web versiyonları',
        'Mobil uygulamalar',
        'Otomatik güncellemeler'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenlik ve Uyumluluk',
      description: 'Gelişmiş güvenlik özellikleri ve uyumluluk araçları',
      features: [
        'Multi-factor authentication',
        'Data Loss Prevention',
        'Compliance Center',
        'Advanced Threat Protection'
      ]
    }
  ];

  const packages = [
    {
      name: 'Business Basic',
      description: 'Küçük işletmeler için temel bulut hizmetleri',
      features: [
        'Exchange Online (50 GB)',
        'Microsoft Teams',
        'SharePoint Online',
        'OneDrive for Business (1 TB)',
        'Web ve mobil Office uygulamaları',
        '7/24 telefon ve web desteği'
      ],
      popular: false,
      color: 'blue'
    },
    {
      name: 'Business Standard',
      description: 'Tam özellikli üretkenlik ve işbirliği araçları',
      features: [
        "Business Basic'teki tüm özellikler",
        'Desktop Office uygulamaları',
        'Outlook Customer Manager',
        'Microsoft Bookings',
        'MileIQ',
        'Gelişmiş güvenlik özellikleri'
      ],
      popular: true,
      color: 'green'
    },
    {
      name: 'Business Premium',
      description: 'Gelişmiş güvenlik ve cihaz yönetimi ile tam çözüm',
      features: [
        "Business Standard'taki tüm özellikler",
        'Microsoft Intune',
        'Azure Information Protection',
        'Advanced Threat Protection',
        'Windows 10 Business',
        'Gelişmiş analitik ve raporlama'
      ],
      popular: false,
      color: 'purple'
    }
  ];

  const migrationSteps = [
    {
      step: '1',
      title: 'Analiz ve Planlama',
      description:
        'Mevcut IT altyapınızı analiz eder ve geçiş planını hazırlarız',
      icon: 'ri-search-line'
    },
    {
      step: '2',
      title: 'Hazırlık ve Yapılandırma',
      description: 'Microsoft 365 ortamınızı kurar ve yapılandırırız',
      icon: 'ri-settings-line'
    },
    {
      step: '3',
      title: 'Veri Geçişi',
      description: 'E-postalar, dosyalar ve diğer verileri güvenle taşırız',
      icon: 'ri-download-cloud-line'
    },
    {
      step: '4',
      title: 'Eğitim ve Destek',
      description: 'Kullanıcılarınızı eğitir ve sürekli destek sağlarız',
      icon: 'ri-graduation-cap-line'
    }
  ];

  const benefits = [
    {
      icon: 'ri-global-line',
      title: 'Her Yerden Erişim',
      description:
        'Ofiste, evde veya yolda - tüm dosyalarınıza ve uygulamalarınıza güvenle erişin'
    },
    {
      icon: 'ri-team-line',
      title: 'Gelişmiş İşbirliği',
      description:
        'Teams ile video konferans, sohbet ve dosya paylaşımı tek platformda'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Kurumsal Güvenlik',
      description: 'Gelişmiş güvenlik özellikleri ile verilerinizi koruyun'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Maliyet Tasarrufu',
      description:
        'Donanım yatırımı yapmadan kurumsal IT altyapısına sahip olun'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Otomatik Güncellemeler',
      description:
        'Her zaman en güncel özellikler ve güvenlik yamaları'
    },
    {
      icon: 'ri-phone-line',
      title: '7/24 Destek',
      description:
        "Microsoft ve Nimbus Bilişim'den kesintisiz teknik destek"
    }
  ];

  const supportServices = [
    {
      icon: 'ri-phone-line',
      title: 'Telefon Desteği',
      description: 'Acil durumlar için anında telefon desteği'
    },
    {
      icon: 'ri-remote-control-line',
      title: 'Uzaktan Destek',
      description: 'Sorunları uzaktan çözüm'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Kullanıcı Eğitimi',
      description: 'Ekibiniz için özel eğitim programları'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-gray-900 sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="flex items-center space-x-3 cursor-pointer"
              >
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg text-gray-900">
                  Nimbus Bulut Bilişim Teknoloji ve Mühendislik
                </div>
              </button>

              <nav className="hidden lg:flex space-x-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowCustomerToolsDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>IT Hizmetleri</span>
                    <i
                      className={`ri-arrow-${showServicesDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                      <div className="grid grid-cols-3 gap-8 px-8">
                        {/* IT Destek Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            IT Destek Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-support');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Destek & Bakım Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">7/24 IT destek ve bakım hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-consultinging');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Profesyonel danışmanlık</div>
                            </button>
                          </div>
                        </div>

                        {/* Bulut Bilişim Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Bulut Bilişim Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/virtual-server');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Kurumsal bulut sunucu hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/managed-it');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Yönetilen IT Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Tüm yönetim hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/migration');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Sistem Taşıma ve Geçiş Hizmetleri (Migration)</div>
                              <div className="text-sm text-gray-500 mt-1">Sunucu taşıma hizmetleri</div>
                            </button>
                          </div>
                        </div>

                        {/* Microsoft 365 Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Microsoft 365 Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/microsoft-365');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                            >
                              <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                              <div className="text-sm text-gray-500 mt-1">Azure Expert hizmetleri</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Başarı Hikayeleri
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/about')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Hakkımızda
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  SSS
                </button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowCallbackModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                İletişim
              </button>
              <a
                href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/0be77a35b5aa0577a21f1fcd69871789.jpg')`,
            animation: 'slowPan 5s ease-in-out infinite alternate'
          }}
        ></div>
        <style>{`
          @keyframes slowPan {
            0% {
              transform: scale(1) translateY(0);
            }
            100% {
              transform: scale(1.1) translateY(3%);
            }
          }
        `}</style>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-4">
              <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                Microsoft 365 Hizmetleri
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Microsoft 365 ile Modern Çalışma Ortamı
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              İşletmenizin üretkenliğini artıran <strong>Microsoft 365 çözümleri</strong> ile{' '}
              <strong>her yerden güvenli erişim</strong>, <strong>gelişmiş işbirliği araçları</strong>{' '}
              ve <strong>profesyonel e-posta sistemi</strong> elde edin. Uzman ekibimiz geçiş sürecini yönetir.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>Ücretsiz Danışmanlık</span>
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Paketleri İncele
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Microsoft 365 Hizmetleri
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Microsoft 365 ekosisteminin tüm bileşenlerini profesyonelce yönetir,
              işletmenizin dijital dönüşümünü destekleriz.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center space-x-2 text-sm text-gray-600"
                    >
                      <i className="ri-check-line text-blue-600 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Neden Microsoft 365?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Microsoft 365 ile işletmenizin verimliliğini artırın ve modern çalışma deneyimi yaşayın.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Microsoft 365 Paketleri
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              İşletmenizin büyüklüğüne ve ihtiyaçlarına uygun Microsoft 365 paketini seçin.
              Tüm paketlerde kurulum, yapılandırma ve sürekli destek dahildir.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                  pkg.popular ? 'ring-4 ring-green-500 transform scale-105' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="bg-green-600 text-white text-center py-2 font-semibold">
                    En Popüler
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-6 text-sm">{pkg.description}</p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <i
                          className={`ri-check-line ${
                            pkg.color === 'green'
                              ? 'text-green-600'
                              : pkg.color === 'purple'
                              ? 'text-purple-600'
                              : 'text-blue-600'
                          } mt-1 flex-shrink-0`}
                        ></i>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      pkg.popular
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : pkg.color === 'purple'
                        ? 'border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white'
                        : 'border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                    }`}
                  >
                    Fiyat Teklifi Al
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Microsoft 365 Geçiş Süreci
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Mevcut sistemlerinizden Microsoft 365'e geçiş sürecini adım adım yönetir,
              kesintisiz bir deneyim sağlarız.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {migrationSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">{step.step}</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Microsoft 365 ile İş Verimliliğinizi Artırın
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Her Yerden Erişim</h4>
                      <p className="text-gray-600">
                        Ofiste, evde veya yolda - tüm dosyalarınıza ve uygulamalarınıza güvenle erişin
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Gelişmiş İşbirliği</h4>
                      <p className="text-gray-600">
                        Teams ile video konferans, sohbet ve dosya paylaşımı tek platformda
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Kurumsal Güvenlik</h4>
                      <p className="text-gray-600">
                        Gelişmiş güvenlik özellikleri ile verilerinizi koruyun
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-money-dollar-circle-line text-blue-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Maliyet Tasarrufu</h4>
                      <p className="text-gray-600">
                        Donanım yatırımı yapmadan kurumsal IT altyapısına sahip olun
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  Detaylı Bilgi Alın
                </button>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20office%20workers%20collaborating%20with%20Microsoft%20365%20applications%2C%20Teams%20video%20conference%2C%20professional%20workplace%20with%20cloud%20productivity%20tools%2C%20business%20meeting%20with%20technology&width=600&height=500&seq=26&orientation=landscape"
                  alt="Microsoft 365 İşbirliği"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">7/24 Microsoft 365 Desteği</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Microsoft 365 kullanımında karşılaştığınız her türlü sorunda yanınızdayız.
              Uzman ekibimiz size özel çözümler sunar.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {supportServices.map((service, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="font-semibold mb-2">{service.title}</h3>
                  <p className="text-blue-100 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer"
            >
              <span>Destek Alın</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Microsoft 365 ile Dijital Dönüşümünüzü Başlatın
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Nimbus Bilişim uzman ekibi ile Microsoft 365 geçişinizi güvenle gerçekleştirin.
              Ücretsiz danışmanlık için hemen iletişime geçin.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <span>Ücretsiz Danışmanlık</span>
                <i className="ri-arrow-right-line"></i>
              </button>
              <button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Paketleri İncele
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg">Nimbus Bulut Bilişim Teknoloji ve Mühendislik</div>
              </div>
              <p className="text-gray-300">
                Türkiye'nin güvenilir IT çözüm ortağı. İşletmenizin teknoloji
                ihtiyaçlarında yanınızdayız.
              </p>
            </div>
            <div>
              <h4 className="text-lg mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <a
                    href="/services/virtual-server"
                    className="hover:text-blue-400 transition-colors"
                  >
                    Sanal Sunucu
                  </a>
                </li>
                <li>
                  <a
                    href="/services/microsoft-365"
                    className="hover:text-blue-400 transition-colors"
                  >
                    Microsoft 365
                  </a>
                </li>
                <li>
                  <a
                    href="/services/it-support"
                    className="hover:text-blue-400 transition-colors"
                  >
                    IT Destek
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg mb-4">Şirket</h4>
              <ul className="space-y-2 text-gray-300">
                <li>
                  <a
                    href="/about"
                    className="hover:text-blue-400 transition-colors"
                  >
                    Hakkımızda
                  </a>
                </li>
                <li>
                  <a
                    href="/#basari-hikayeleri"
                    className="hover:text-blue-400 transition-colors"
                  >
                    Başarı Hikayeleri
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@nimbusbilisim.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>İzmir, Türkiye</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              © 2024 Nimbus Bulut Bilişim. Tüm hakları saklıdır. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-blue-400 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                    alt="Nimbus Bilişim Logo"
                    className="h-12 w-auto"
                  />
                  <h3 className="text-2xl font-bold text-gray-900">
                    Microsoft 365 Danışmanlığı
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsContactModalOpen(false);
                    setSubmitStatus(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                id="microsoft-365-form"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Telefon numaranız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="E-posta adresiniz"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Şirket *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Şirket adınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Microsoft 365 İhtiyacınız
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Kaç kullanıcı? Hangi uygulamalara ihtiyacınız var? Mevcut e-posta sisteminiz var mı? (maksimum 500 karakter)"
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg transition-colors mt-6 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Danışmanlık Talep Et'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
